import { useMutation } from '@apollo/client';
import type { DataImportValidateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DataImportValidateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDataImportValidateTRPC } from './useDataImportValidateTRPC';

/**
 * Flag-gated wrapper for the `dataImportValidate` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `DataImportValidateMutation` shape.
 *
 * NOTE: the tRPC path is ASYNC — it resolves as soon as the validation
 * pipeline has been kicked off (status moves to 'validating' and the results
 * page's status subscription shows live progress), whereas the legacy Hasura
 * action ran validation synchronously. Consumers must not assume validation
 * is complete when the promise resolves.
 */
export const useDataImportValidate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [validateGraphQL, graphqlState] = useMutation(
    DataImportValidateDocument,
    {
      update: (cache) => {
        evictField(cache, 'data_import');
      },
    }
  );

  const trpcMutation = useDataImportValidateTRPC();

  const dataImportValidate = async (
    id: string
  ): Promise<DataImportValidateMutation> => {
    if (trpcEnabled) {
      return trpcMutation.dataImportValidate(id);
    }

    const result = await validateGraphQL({ variables: { Id: id } });
    if (!result.data) {
      throw new Error('Failed to validate data import');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      dataImportValidate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    dataImportValidate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
